/** /onboarding_invites/ — the dashboard self-signup invite links (backend plan
 *  2026-10-17-dashboard-self-signup-invite-links §5, §11, §15).
 *
 *  Ops issue a link (one, or a batch of up to 100 for QR sheets), e-mail a bound one, and follow
 *  what happens with it. Reads need `console_onboarding_invites_view`; every write and the link
 *  need `console_onboarding_invites_manage`. Every write is audited server-side
 *  (`onboarding_invite.*`).
 *
 *  THE LINK IS A CREDENTIAL until the invite is used: list and retrieve never carry it. It comes
 *  from two places only — the create response (the creator's own new invites, for the result
 *  dialog and the QR print sheet) and `link(id)`, which writes `onboarding_invite.link_revealed`
 *  on every call. Callers call these two directly from the event handler and keep the answer in
 *  component state: never a query key, a query or mutation cache entry, router state or storage
 *  (lessons: "A credential must not ride useMutation").
 *
 *  Shapes come from the generated spec types; never hand-mirror them. */
import { apiGet, apiPatch, apiPost, ConsoleApiError, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';

type Schemas = components['schemas'];

export type InviteStatus = Schemas['OnboardingInviteStatus'];
export type InviteListItem = Schemas['ConsoleOnboardingInviteListItem'];
export type InviteDetail = Schemas['ConsoleOnboardingInviteDetail'];
export type InviteContextSummary = Schemas['OnboardingInviteContextSummary'];
export type InviteContextKey = Schemas['OnboardingInviteContextKey'];
export type InviteCreate = Schemas['ConsoleOnboardingInviteCreate'];
export type InviteCreateResponse = Schemas['ConsoleOnboardingInviteCreateResponse'];
/** A new invite as the create response lists it: the detail plus its link. */
export type CreatedInvite = InviteCreateResponse['invites'][number];
export type InviteUpdate = Schemas['ConsoleOnboardingInviteUpdate'];
export type InviteLink = Schemas['ConsoleOnboardingInviteLink'];
export type InviteErrorBody = Schemas['ConsoleOnboardingInviteError'];
export type InviteErrorCode = InviteErrorBody['code'];
export type InviteTimeline = Schemas['ConsoleOnboardingInviteTimeline'];
export type InviteTimelineItem = InviteTimeline['items'][number];

/** Every status, in the backend's precedence (revoked > live > used > expired > claimed > available)
 *  reversed into lifecycle order; `satisfies` keeps it exhaustive against the spec enum. */
export const INVITE_STATUS_LABELS = {
  available: 'Available',
  claimed: 'Claimed',
  expired: 'Expired',
  used: 'Used',
  live: 'Live',
  revoked: 'Revoked',
} as const satisfies Record<InviteStatus, string>;

export const INVITE_STATUSES = Object.keys(INVITE_STATUS_LABELS) as InviteStatus[];

export type InviteSource = InviteListItem['source'];

/** Who issued an invite; `satisfies` keeps it exhaustive against the spec enum. `marketplace`:
 *  minted by a booking system's marketplace app install, with no ops step (backend plan
 *  2026-10-22-easy-onboarding-bokun §6.4). */
export const INVITE_SOURCE_LABELS = {
  console: 'Console',
  automation: 'Automation',
  api: 'API',
  marketplace: 'Marketplace install',
} as const satisfies Record<InviteSource, string>;

export const INVITE_SOURCES = Object.keys(INVITE_SOURCE_LABELS) as InviteSource[];

/** The context key only a marketplace install sets (`{vendor, account, label}`): create refuses
 *  it, and an edit must send it unchanged (else 400). */
export const MARKETPLACE_CONTEXT_KEY = 'marketplace';

/** The install that minted an invite: the booking system's code, the vendor's account there, and
 *  its label (Bókun: the vendor's domain). */
export interface InviteMarketplace {
  vendor: string;
  account: string;
  label: string | null;
}

/** `context.marketplace` when it has the install's shape, else null. */
export function inviteMarketplace(context: Record<string, unknown> | null | undefined): InviteMarketplace | null {
  const raw = context?.[MARKETPLACE_CONTEXT_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.vendor !== 'string' || !m.vendor) return null;
  return {
    vendor: m.vendor,
    account: typeof m.account === 'string' || typeof m.account === 'number' ? String(m.account) : '',
    label: typeof m.label === 'string' && m.label ? m.label : null,
  };
}

/** At most this many invites per create (D14). */
export const MAX_QUANTITY = 100;
export const LABEL_MAX = 200;

/** Under one root, so a `['onboardingInvites']` invalidation reaches the grid, the drawer and the
 *  timeline. */
export const invitesKeys = {
  all: ['onboardingInvites'] as const,
  list: () => ['onboardingInvites', 'list'] as const,
  detail: (id: number) => ['onboardingInvites', 'detail', id] as const,
  timeline: (id: number) => ['onboardingInvites', 'timeline', id] as const,
};

export interface InviteListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  /** id|label|email|created_ts|expires_ts|claimed_ts|sent_ts|used_ts|completed_ts ('-' desc).
   *  Default -created_ts. */
  ordering?: string;
  filters?: WireNode[];
}

function toQuery(p: InviteListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(p: InviteListParams = {}): Promise<PaginatedResponse<InviteListItem>> {
  const qs = toQuery(p);
  return apiGet(`/onboarding_invites/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

export async function retrieve(id: number): Promise<InviteDetail> {
  return apiGet(`/onboarding_invites/${id}/`);
}

/** POST — one invite, or a batch (`quantity` > 1, unbound only). The 201 carries the links:
 *  keep it in component state only. `send` e-mails a bound invite at once; a failed send is
 *  reported in `send` and keeps the link valid. */
export async function create(body: InviteCreate): Promise<InviteCreateResponse> {
  return apiPost(`/onboarding_invites/`, body);
}

/** PATCH label / email / expiry / context while the invite is not used (409 `invite_used`). */
export async function update(id: number, body: InviteUpdate): Promise<InviteDetail> {
  return apiPatch(`/onboarding_invites/${id}/`, body);
}

/** The invite's link (copy / QR). Audited per call — fetch it only when the operator asks. */
export async function link(id: number): Promise<InviteLink> {
  return apiGet(`/onboarding_invites/${id}/link/`);
}

/** E-mail the link to a bound invite (send / resend). 503 `invite_mail_failed`: not sent, the
 *  link stays valid. */
export async function send(id: number): Promise<InviteDetail> {
  return apiPost(`/onboarding_invites/${id}/send/`, {});
}

/** New code (code_version + 1): the old link stops working at once; a holder's claim is kept. */
export async function rotate(id: number): Promise<InviteDetail> {
  return apiPost(`/onboarding_invites/${id}/rotate/`, {});
}

export async function revoke(id: number, reason: string): Promise<InviteDetail> {
  return apiPost(`/onboarding_invites/${id}/revoke/`, reason ? { reason } : {});
}

/** Available again; an interrupted claim is released. */
export async function reinstate(id: number): Promise<InviteDetail> {
  return apiPost(`/onboarding_invites/${id}/reinstate/`, {});
}

/** Force-release a claim: the holder's session dies and any browser can claim the link again. */
export async function release(id: number): Promise<InviteDetail> {
  return apiPost(`/onboarding_invites/${id}/release/`, {});
}

export async function timeline(id: number): Promise<InviteTimeline> {
  return apiGet(`/onboarding_invites/${id}/timeline/`);
}

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/** Field errors of a refused create / edit, flattened for the form: `label`, `email`,
 *  `expires_ts`, `quantity` → a sentence; `context` → a sentence for the whole object, or
 *  `context.<key>` per key. Covers both shapes the backend answers 400 with: the service's
 *  `{code: invite_invalid, errors: {field: "...", context: {key: "..."}}}` and DRF's
 *  `{field: ["..."]}`. */
export function inviteFieldErrors(e: unknown): Record<string, string> {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return {};
  const body = (e.body ?? {}) as Record<string, unknown>;
  const source =
    body.code === 'invite_invalid' && body.errors && typeof body.errors === 'object'
      ? (body.errors as Record<string, unknown>)
      : body;
  const out: Record<string, string> = {};
  for (const [field, value] of Object.entries(source)) {
    if (field === 'detail' || field === 'code') continue;
    if (typeof value === 'string') out[field] = value;
    else if (Array.isArray(value) && typeof value[0] === 'string') out[field] = value[0];
    else if (field === 'context' && value && typeof value === 'object') {
      for (const [key, msg] of Object.entries(value as Record<string, unknown>)) {
        if (typeof msg === 'string') out[key === 'context' ? 'context' : `context.${key}`] = msg;
      }
    }
  }
  return out;
}

/** A readable sentence for a refused invite call: the backend's `detail` (every typed refusal
 *  carries one), else the first field error, else `fallback`. */
export function inviteErrorMessage(e: unknown, fallback: string): string {
  if (!(e instanceof ConsoleApiError)) return fallback;
  if (e.body?.detail) return e.body.detail;
  const fields = Object.values(inviteFieldErrors(e));
  if (fields[0]) return fields[0];
  return e.status ? `${fallback} (HTTP ${e.status})` : fallback;
}

/** The typed refusal code of an invite call, if any. */
export function inviteErrorCode(e: unknown): InviteErrorCode | null {
  if (!(e instanceof ConsoleApiError)) return null;
  return (e.body?.code as InviteErrorCode | undefined) ?? null;
}
